import React from "react";
import { handleScanResult } from "../../helpers/handleScanResultUtils";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import HoneywellScannerPagePresentation from "../honeywell-scanner-page/HoneywellScannerPagePresentation";
import { SymbologyType } from "../../types/SymbologyType";
import { useHDIScanner } from "../../hooks/useHDIScanner";
import { ScanResultType } from "../../types/ScanResultType";
import { useDefaultScannerBehavior } from "../../hooks/useDefaultScannerBehavior";
import {
  useScannerInput,
  processScanResult,
} from "../../hooks/useScannerInput";
import { useDebouncedValue } from "@coworker/app/src/hooks/useDebounce";

const HDIScannerPage: React.FC = () => {
  const inputFocusRef = React.useRef<HTMLInputElement>(null);
  const navigate = useFixaNavigate();
  const { navigateToSearch, error, setError, taskType, readyToScanText } =
    useDefaultScannerBehavior();
  useHDIScanner((data, type) => {
    const scanResult: ScanResultType = {
      scannedBarcode: data,
      symbologyFormat: type as SymbologyType,
    };
    handleScanResult(scanResult, taskType, navigate);
  });

  const [debouncedInput, setDebouncedInput] = React.useState("");
  const debouncedValue = useDebouncedValue(debouncedInput, 200);

  const handleInputChange = useScannerInput((value, symbologyFormat) => {
    processScanResult(value, symbologyFormat, taskType, navigate);
  });

  React.useEffect(() => {
    if (debouncedValue.length > 0) {
      handleInputChange({
        target: { value: debouncedValue },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [debouncedValue, handleInputChange]);

  React.useEffect(() => {
    if (inputFocusRef.current) {
      inputFocusRef.current.focus();
    }
  }, [inputFocusRef]);

  return (
    <>
      <input
        ref={inputFocusRef}
        type="text"
        id="scanner-text-input"
        aria-hidden="true"
        onChange={(e) => setDebouncedInput(e.target.value)}
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          border: 0,
        }}
      />
      <HoneywellScannerPagePresentation
        error={error}
        setError={setError}
        readyToScanText={readyToScanText}
        scannerType="hdi"
        taskType={taskType}
        pushSearch={navigateToSearch}
      />
    </>
  );
};

export default HDIScannerPage;
